import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { collection, query, where, orderBy, limit, getDocs, doc, deleteDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../lib/firebase';
import { 
  SparklesIcon, 
  DocumentTextIcon, 
  UserIcon, 
  BuildingOffice2Icon,
  CalendarIcon,
  CurrencyDollarIcon,
  ClockIcon,
  PaperAirplaneIcon,
  EyeIcon,
  TrashIcon,
  Cog6ToothIcon,
  ExclamationTriangleIcon,
  PlusIcon,
  DocumentDuplicateIcon,
  TagIcon
} from '@heroicons/react/24/outline';
import LoadingScreen from '../components/common/LoadingScreen';
import { Card } from '../components/common/Card';
import { Input } from '../components/common/Input';
import { Button } from '../components/common/Button';
import Modal from '../components/common/Modal';
import CustomerSelector from '../components/common/CustomerSelector';
import TemplateSelector from '../components/ai/TemplateSelector';
import TemplateManager from '../components/ai/TemplateManager';
import CommonPageHeader from '../components/common/CommonPageHeader';
import ReactMarkdown from 'react-markdown';
import { toast } from 'react-hot-toast';
import { Customer } from '../types/customer';
import { AIQuotationTemplate, AIQuotationParameters, AIQuotation } from '../types/aiQuotation';
import { Link } from 'react-router-dom';

const AIQuotationGenerator: React.FC = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  
  // State management
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [aiConfigured, setAiConfigured] = useState(true); // Always true since we have .env fallback
  
  // Customer and template selection
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<AIQuotationTemplate | null>(null);
  const [templates, setTemplates] = useState<AIQuotationTemplate[]>([]);
  
  // Form parameters
  const [parameters, setParameters] = useState<AIQuotationParameters>({
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    customerAddress: '',
    projectName: '',
    projectDescription: '',
    deliveryDate: '',
    validityPeriod: '30 nap',
    paymentTerms: 'Előre utalás',
    templateVariables: {}
  });
  
  const [customPrompt, setCustomPrompt] = useState('');
  const [quotations, setQuotations] = useState<AIQuotation[]>([]);
  const [selectedQuotation, setSelectedQuotation] = useState<AIQuotation | null>(null);
  const [showViewer, setShowViewer] = useState(false);
  const [showTemplateManager, setShowTemplateManager] = useState(false);

  useEffect(() => {
    checkAIConfiguration();
    loadQuotations();
    loadTemplates();
  }, [user]);

  const checkAIConfiguration = async () => {
    // AI is always configured due to .env fallback
    setAiConfigured(true);
  };

  const loadTemplates = async () => {
    // TODO: Implement template loading from Firebase
    // For now, use mock data
    const mockTemplates: AIQuotationTemplate[] = [
      {
        id: '1',
        name: 'Általános szolgáltatás',
        description: 'Általános szolgáltatási ajánlat sablon',
        organizationId: user?.organizationId || '',
        category: 'service',
        variables: [
          { key: 'serviceType', label: 'Szolgáltatás típusa', type: 'text', required: true },
          { key: 'duration', label: 'Időtartam', type: 'text', required: true },
          { key: 'warranty', label: 'Garancia', type: 'text', required: false, defaultValue: '12 hónap' }
        ],
        content: `# Ajánlat - {{projectName}}

Tisztelt {{customerName}}!

Köszönjük érdeklődését szolgáltatásaink iránt. Az alábbiakban részletes ajánlatunkat találja:

## Szolgáltatás részletei
- **Típus**: {{serviceType}}
- **Időtartam**: {{duration}}
- **Garancia**: {{warranty}}

## Projekt leírása
{{projectDescription}}

## Szállítási határidő
{{deliveryDate}}

## Fizetési feltételek
{{paymentTerms}}

Az ajánlat {{validityPeriod}} érvényes.

Tisztelettel,
Csapatunk`,
        isActive: true,
        createdAt: new Date(),
        updatedAt: new Date(),
        createdBy: user?.uid || '',
        usageCount: 0
      },
      {
        id: '2',
        name: 'Termék értékesítés',
        description: 'Termék értékesítési ajánlat sablon',
        organizationId: user?.organizationId || '',
        category: 'product',
        variables: [
          { key: 'productName', label: 'Termék neve', type: 'text', required: true },
          { key: 'quantity', label: 'Mennyiség', type: 'number', required: true },
          { key: 'unitPrice', label: 'Egységár', type: 'number', required: true }
        ],
        content: `# Termék ajánlat - {{projectName}}

Tisztelt {{customerName}}!

## Termék részletei
- **Termék**: {{productName}}
- **Mennyiség**: {{quantity}} db
- **Egységár**: {{unitPrice}} Ft

## Szállítási információk
- **Szállítási cím**: {{customerAddress}}
- **Szállítási határidő**: {{deliveryDate}}

## Fizetési feltételek
{{paymentTerms}}

Az ajánlat {{validityPeriod}} érvényes.

Tisztelettel,
Értékesítési csapatunk`,
        isActive: true,
        createdAt: new Date(),
        updatedAt: new Date(),
        createdBy: user?.uid || '',
        usageCount: 0
      }
    ];
    setTemplates(mockTemplates);
  };

  const loadQuotations = async () => {
    if (!user?.organizationId) return;

    try {
      setLoading(true);
      
      // Közvetlenül lekérjük a Firestore-ból
      const quotationsRef = collection(db, 'aiQuotations');
      const q = query(
        quotationsRef,
        where('organizationId', '==', user.organizationId),
        orderBy('createdAt', 'desc'),
        limit(50)
      );
      
      const snapshot = await getDocs(q);
      const quotationsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.() || doc.data().createdAt,
        updatedAt: doc.data().updatedAt?.toDate?.() || doc.data().updatedAt
      }));
      
      setQuotations(quotationsData as any);
    } catch (error) {
      console.error('Error loading AI quotations:', error);
      toast.error('Hiba az ajánlatok betöltésekor');
    } finally {
      setLoading(false);
    }
  };

  // Update parameters when customer is selected
  useEffect(() => {
    if (selectedCustomer) {
      const customerName = selectedCustomer.type === 'organization' 
        ? selectedCustomer.companyName || ''
        : `${selectedCustomer.lastName} ${selectedCustomer.firstName}`;
      
      const address = selectedCustomer.address 
        ? `${selectedCustomer.address.street || ''}, ${selectedCustomer.address.city || ''} ${selectedCustomer.address.postalCode || ''}`.trim()
        : '';

      setParameters(prev => ({
        ...prev,
        customerId: selectedCustomer.id,
        customerName,
        customerEmail: selectedCustomer.email || '',
        customerPhone: selectedCustomer.phone || '',
        customerAddress: address
      }));
    }
  }, [selectedCustomer]);

  // Update parameters when template is selected
  useEffect(() => {
    if (selectedTemplate) {
      setParameters(prev => ({
        ...prev,
        templateId: selectedTemplate.id,
        templateVariables: {}
      }));
    }
  }, [selectedTemplate]);

  const handleParameterChange = (key: keyof AIQuotationParameters, value: string) => {
    setParameters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleTemplateVariableChange = (key: string, value: string) => {
    setParameters(prev => ({
      ...prev,
      templateVariables: {
        ...prev.templateVariables,
        [key]: value
      }
    }));
  };

  const generateQuotation = async () => {
    if (!user?.organizationId) return;

    // Validation
    if (!parameters.customerName.trim()) {
      toast.error('Kérjük adja meg az ügyfél nevét');
      return;
    }

    if (!parameters.projectName.trim()) {
      toast.error('Kérjük adja meg a projekt nevét');
      return;
    }

    try {
      setGenerating(true);
      const generateAIQuotation = httpsCallable(functions, 'generateAIQuotation');
      
      const requestData = {
        organizationId: user.organizationId,
        userId: user.uid,
        customerId: selectedCustomer?.id,
        templateId: selectedTemplate?.id,
        parameters,
        customPrompt: customPrompt.trim() || undefined
      };

      const result = await generateAIQuotation(requestData);
      
      if ((result.data as any)?.success) {
        toast.success('Ajánlat sikeresen generálva!');
        await loadQuotations();
        
        // Clear form
        if (!selectedCustomer) {
          setParameters({
            customerName: '',
            customerEmail: '',
            customerPhone: '',
            customerAddress: '',
            projectName: '',
            projectDescription: '',
            deliveryDate: '',
            validityPeriod: '30 nap',
            paymentTerms: 'Előre utalás',
            templateVariables: {}
          });
        }
        setCustomPrompt('');
      } else {
        toast.error((result.data as any)?.error || 'Hiba történt az ajánlat generálása során');
      }
    } catch (error: any) {
      console.error('Error generating quotation:', error);
      toast.error(error.message || 'Hiba történt az ajánlat generálása során');
    } finally {
      setGenerating(false);
    }
  };

  const deleteQuotation = async (id: string) => {
    if (!window.confirm('Biztosan törli ezt az ajánlatot?')) return;

    try {
      // Közvetlenül töröljük a Firestore-ból
      await deleteDoc(doc(db, 'aiQuotations', id));
      toast.success('Ajánlat törölve');
      await loadQuotations();
    } catch (error) {
      console.error('Error deleting quotation:', error);
      toast.error('Hiba történt a törlés során');
    }
  };

  const viewQuotation = (quotation: AIQuotation) => {
    setSelectedQuotation(quotation);
    setShowViewer(true);
  };

  if (loading) {
    return <LoadingScreen />;
  }

  if (!aiConfigured) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100/50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900/50">
        <CommonPageHeader
          title="AI Árajánlatok"
          subtitle="Automatikus ajánlat generálás Claude AI-val"
          icon={SparklesIcon}
        />
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Card className="text-center py-12">
            <ExclamationTriangleIcon className="h-12 w-12 text-amber-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
              AI nincs konfigurálva
            </h3>
            <p className="text-gray-500 dark:text-gray-400 mb-6">
              Az AI ajánlat generálás használatához konfigurálni kell a Claude API kulcsot.
            </p>
            <Link to="/settings">
              <Button>
                <Cog6ToothIcon className="h-4 w-4 mr-2" />
                Beállítások megnyitása
              </Button>
            </Link>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100/50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900/50">
      <CommonPageHeader
        title="AI Árajánlatok"
        subtitle="Automatikus ajánlat generálás Claude AI-val"
        icon={SparklesIcon}
      >
        <Link to="/settings">
          <Button variant="outline" className="bg-white/20 backdrop-blur-sm text-white border-white/30 hover:bg-white/30">
            <Cog6ToothIcon className="h-4 w-4 mr-2" />
            <span>AI Beállítások</span>
          </Button>
        </Link>
      </CommonPageHeader>

      <div className="px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          {/* Left column - Form */}
          <div className="xl:col-span-2 space-y-6">
            {/* Customer and Template Selection - Combined */}
            <Card>
              <div className="p-6">
                <div className="flex items-center mb-6">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-gradient-to-br from-blue-100 to-purple-100 dark:from-blue-900/20 dark:to-purple-900/20 rounded-lg">
                      <UserIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                      <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                        Ügyfél és Sablon
                      </h2>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Válasszon ügyfelet és sablont az ajánlat generálásához
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-6">
                  {/* Customer Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                      Ügyfél kiválasztása
                    </label>
                    <CustomerSelector
                      selectedCustomer={selectedCustomer}
                      onCustomerSelect={setSelectedCustomer}
                      placeholder="Válasszon ügyfelet vagy töltse ki manuálisan..."
                    />
                  </div>

                  {/* Template Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                      Sablon kiválasztása (opcionális)
                    </label>
                    <TemplateSelector
                      templates={templates}
                      selectedTemplate={selectedTemplate}
                      onTemplateSelect={setSelectedTemplate}
                    />
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    Gyors műveletek
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setShowTemplateManager(true)}
                      className="flex items-center space-x-2"
                    >
                      <DocumentDuplicateIcon className="h-4 w-4" />
                      <span>Sablonok kezelése</span>
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => {/* TODO: Korábbi ajánlatok */}}
                      className="flex items-center space-x-2"
                    >
                      <DocumentTextIcon className="h-4 w-4" />
                      <span>Ajánlat előzmények</span>
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => {/* TODO: Exportálás */}}
                      className="flex items-center space-x-2"
                    >
                      <PaperAirplaneIcon className="h-4 w-4" />
                      <span>Tömeges exportálás</span>
                    </Button>
                  </div>
                </div>
              </div>
            </Card>

            {/* Basic Information */}
            <Card>
              <div className="flex">
                <div className="p-4 bg-gradient-to-br from-green-100 to-teal-100 dark:from-green-900/20 dark:to-teal-900/20 flex items-center justify-center rounded-l-lg border-r border-gray-200 dark:border-gray-600">
                  <DocumentTextIcon className="w-5 h-5 text-green-600 dark:text-green-400" />
                </div>
                <div className="p-4 flex-1 space-y-4">
                  <h3 className="text-sm font-medium text-gray-800 dark:text-gray-200">
                    Alapinformációk
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Ügyfél neve *
                      </label>
                      <Input
                        value={parameters.customerName}
                        onChange={(e) => handleParameterChange('customerName', e.target.value)}
                        placeholder="Ügyfél neve"
                        disabled={!!selectedCustomer}
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Email cím
                      </label>
                      <Input
                        type="email"
                        value={parameters.customerEmail}
                        onChange={(e) => handleParameterChange('customerEmail', e.target.value)}
                        placeholder="email@example.com"
                        disabled={!!selectedCustomer}
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Telefon
                      </label>
                      <Input
                        value={parameters.customerPhone}
                        onChange={(e) => handleParameterChange('customerPhone', e.target.value)}
                        placeholder="+36 30 123 4567"
                        disabled={!!selectedCustomer}
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Cím
                      </label>
                      <Input
                        value={parameters.customerAddress}
                        onChange={(e) => handleParameterChange('customerAddress', e.target.value)}
                        placeholder="1234 Budapest, Példa utca 1."
                        disabled={!!selectedCustomer}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </Card>

            {/* Project Details */}
            <Card>
              <div className="flex">
                <div className="p-4 bg-gradient-to-br from-orange-100 to-red-100 dark:from-orange-900/20 dark:to-red-900/20 flex items-center justify-center rounded-l-lg border-r border-gray-200 dark:border-gray-600">
                  <BuildingOffice2Icon className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                </div>
                <div className="p-4 flex-1 space-y-4">
                  <h3 className="text-sm font-medium text-gray-800 dark:text-gray-200">
                    Projekt részletei
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Projekt neve *
                      </label>
                      <Input
                        value={parameters.projectName}
                        onChange={(e) => handleParameterChange('projectName', e.target.value)}
                        placeholder="Projekt neve"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Szállítási határidő
                      </label>
                      <Input
                        value={parameters.deliveryDate}
                        onChange={(e) => handleParameterChange('deliveryDate', e.target.value)}
                        placeholder="2024. március 15."
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Érvényesség
                      </label>
                      <Input
                        value={parameters.validityPeriod}
                        onChange={(e) => handleParameterChange('validityPeriod', e.target.value)}
                        placeholder="30 nap"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Fizetési feltételek
                      </label>
                      <Input
                        value={parameters.paymentTerms}
                        onChange={(e) => handleParameterChange('paymentTerms', e.target.value)}
                        placeholder="Előre utalás"
                      />
                    </div>
                    
                    <div className="md:col-span-2 lg:col-span-4">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Projekt leírása
                      </label>
                      <textarea
                        value={parameters.projectDescription}
                        onChange={(e) => handleParameterChange('projectDescription', e.target.value)}
                        rows={3}
                        className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200 placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                        placeholder="Részletes projekt leírás..."
                      />
                    </div>
                  </div>
                </div>
              </div>
            </Card>

            {/* Template Variables */}
            {selectedTemplate && selectedTemplate.variables.length > 0 && (
              <Card>
                <div className="flex">
                  <div className="p-4 bg-gradient-to-br from-amber-100 to-yellow-100 dark:from-amber-900/20 dark:to-yellow-900/20 flex items-center justify-center rounded-l-lg border-r border-gray-200 dark:border-gray-600">
                    <TagIcon className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                  </div>
                  <div className="p-4 flex-1 space-y-4">
                    <h3 className="text-sm font-medium text-gray-800 dark:text-gray-200">
                      Sablon változók - {selectedTemplate.name}
                    </h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                      {selectedTemplate.variables.map((variable) => (
                        <div key={variable.key}>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            {variable.label} {variable.required && '*'}
                          </label>
                          {variable.type === 'textarea' ? (
                            <textarea
                              value={parameters.templateVariables?.[variable.key] || variable.defaultValue || ''}
                              onChange={(e) => handleTemplateVariableChange(variable.key, e.target.value)}
                              placeholder={variable.placeholder || variable.description}
                              rows={3}
                              className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200 placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                            />
                          ) : variable.type === 'select' && variable.options ? (
                            <select
                              value={parameters.templateVariables?.[variable.key] || variable.defaultValue || ''}
                              onChange={(e) => handleTemplateVariableChange(variable.key, e.target.value)}
                              className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                            >
                              <option value="">Válasszon...</option>
                              {variable.options.map((option) => (
                                <option key={option} value={option}>{option}</option>
                              ))}
                            </select>
                          ) : (
                            <Input
                              type={variable.type === 'number' ? 'number' : variable.type === 'date' ? 'date' : 'text'}
                              value={parameters.templateVariables?.[variable.key] || variable.defaultValue || ''}
                              onChange={(e) => handleTemplateVariableChange(variable.key, e.target.value)}
                              placeholder={variable.placeholder || variable.description}
                            />
                          )}
                          {variable.description && (
                            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                              {variable.description}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </Card>
            )}

            {/* Custom Prompt */}
            <Card>
              <div className="flex">
                <div className="p-4 bg-gradient-to-br from-indigo-100 to-purple-100 dark:from-indigo-900/20 dark:to-purple-900/20 flex items-center justify-center rounded-l-lg border-r border-gray-200 dark:border-gray-600">
                  <SparklesIcon className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                </div>
                <div className="p-4 flex-1 space-y-4">
                  <h3 className="text-sm font-medium text-gray-800 dark:text-gray-200">
                    Egyedi instrukciók (opcionális)
                  </h3>
                  <textarea
                    value={customPrompt}
                    onChange={(e) => setCustomPrompt(e.target.value)}
                    rows={3}
                    className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200 placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                    placeholder="További instrukciók az AI számára (pl. speciális feltételek, stílus, hangsúlyok)..."
                  />
                </div>
              </div>
            </Card>

            {/* Generate Button */}
            <div className="flex justify-end">
              <Button
                onClick={generateQuotation}
                disabled={generating || !parameters.customerName.trim() || !parameters.projectName.trim()}
                className="flex items-center space-x-2"
              >
                {generating ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Generálás...</span>
                  </>
                ) : (
                  <>
                    <PaperAirplaneIcon className="h-4 w-4" />
                    <span>AI Ajánlat Generálása</span>
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Right column - Generated Quotations and Tools */}
          <div className="xl:col-span-1 space-y-6">
            {/* Statistics */}
            <Card>
              <div className="p-4">
                <div className="flex items-center mb-4">
                  <div className="p-2 bg-gradient-to-br from-green-100 to-teal-100 dark:from-green-900/20 dark:to-teal-900/20 rounded-lg mr-3">
                    <CurrencyDollarIcon className="w-5 h-5 text-green-600 dark:text-green-400" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                    Statisztikák
                  </h3>
                </div>
                
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Összes ajánlat</span>
                    <span className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                      {quotations.length}
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Aktív sablonok</span>
                    <span className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                      {templates.filter(t => t.isActive).length}
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Token használat</span>
                    <span className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                      {quotations.reduce((sum, q) => sum + (q.tokensUsed || 0), 0).toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>
            </Card>

            {/* Generated Quotations */}
            <Card>
              <div className="p-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
                  Legutóbbi ajánlatok
                </h3>
                
                {quotations.length === 0 ? (
                  <div className="text-center py-8">
                    <DocumentTextIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Még nincsenek generált ajánlatok
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {quotations.map((quotation) => (
                      <div
                        key={quotation.id}
                        className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                              {quotation.parameters.projectName}
                            </h4>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              {quotation.parameters.customerName}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {(() => {
                                if (quotation.createdAt instanceof Date) {
                                  return quotation.createdAt.toLocaleDateString('hu-HU');
                                }
                                if (quotation.createdAt?.toDate) {
                                  return quotation.createdAt.toDate().toLocaleDateString('hu-HU');
                                }
                                if (quotation.createdAt) {
                                  return new Date(quotation.createdAt as any).toLocaleDateString('hu-HU');
                                }
                                return 'Ismeretlen dátum';
                              })()}
                            </p>
                          </div>
                          <div className="flex items-center space-x-1 ml-2">
                            <button
                              onClick={() => viewQuotation(quotation)}
                              className="p-1.5 text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                              title="Megtekintés"
                            >
                              <EyeIcon className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => deleteQuotation(quotation.id)}
                              className="p-1.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                              title="Törlés"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Card>
          </div>
        </div>
      </div>

      {/* Quotation Viewer Modal */}
      <Modal
        isOpen={showViewer}
        onClose={() => {
          setShowViewer(false);
          setSelectedQuotation(null);
        }}
        title={selectedQuotation ? `Ajánlat: ${selectedQuotation.parameters.projectName}` : 'Ajánlat'}
        size="4xl"
      >
        {selectedQuotation && (
          <div className="p-6">
            <div className="prose dark:prose-invert max-w-none">
              <ReactMarkdown>{selectedQuotation.generatedContent}</ReactMarkdown>
            </div>
            
            <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
                <span>Token használat: {selectedQuotation.tokensUsed}</span>
                <span>
                  Létrehozva: {(() => {
                    if (selectedQuotation.createdAt instanceof Date) {
                      return selectedQuotation.createdAt.toLocaleString('hu-HU');
                    }
                    if (selectedQuotation.createdAt?.toDate) {
                      return selectedQuotation.createdAt.toDate().toLocaleString('hu-HU');
                    }
                    if (selectedQuotation.createdAt) {
                      return new Date(selectedQuotation.createdAt as any).toLocaleString('hu-HU');
                    }
                    return 'Ismeretlen dátum';
                  })()}
                </span>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Template Manager Modal */}
      <TemplateManager
        isOpen={showTemplateManager}
        onClose={() => setShowTemplateManager(false)}
        templates={templates}
        onTemplateCreate={(templateData) => {
          // TODO: Implement template creation
          console.log('Create template:', templateData);
        }}
        onTemplateUpdate={(id, templateData) => {
          // TODO: Implement template update
          console.log('Update template:', id, templateData);
        }}
        onTemplateDelete={(id) => {
          // TODO: Implement template deletion
          console.log('Delete template:', id);
        }}
        onTemplateRefresh={() => {
          loadTemplates();
        }}
      />
    </div>
  );
};

export default AIQuotationGenerator;